import {
  type ApiErrorCode,
  type ApiFailure,
  type PageAccessAction,
  isApiFailure,
  isPageAccessDeniedCode,
  pageAccessActionFor,
} from '@perfectpost/perfect-post-common';

/**
 * Retrouve l'`ApiFailure` derrière ce qu'un thunk a rejeté ou ce qu'un `unwrap()` a levé :
 * la valeur elle-même, ou son `payload` (action rejetée de Redux Toolkit).
 */
export function getApiFailure(error: unknown): ApiFailure | undefined {
  if (isApiFailure(error)) {
    return error;
  }
  if (typeof error === 'object' && error !== null && isApiFailure((error as {payload?: unknown}).payload)) {
    return (error as {payload: ApiFailure}).payload;
  }
  return undefined;
}

/**
 * Fabrique un `ApiFailure` pour un refus qui n'est pas arrivé du serveur sous cette forme :
 * une réponse legacy `{error}` renvoyée en 200 (`ListPost`, `GetActivity`), ou une
 * vérification locale (catégorie en double). `statusCode` 0 = pas de statut HTTP à montrer.
 *
 * `details.localMessage` : `message` est déjà le texte à montrer, écrit par l'appelant —
 * `apiFailureMessage` l'affiche tel quel au lieu de la traduction générique du code.
 */
export function apiFailureFrom(message: string, code: ApiErrorCode = 'internal_error', statusCode = 0): ApiFailure {
  return {ok: false, statusCode, code, message, details: {localMessage: true}};
}

/**
 * L'action à proposer avec un refus, déduite de son code — jamais de son texte.
 * Les refus de page ont leur propre table (`pageAccessActionFor`) ; le reste se range en
 * trois familles : c'est mon plan (`upgrade`), ce sont mes accès LinkedIn (`reconnect`),
 * c'est un réglage d'équipe (`teamSettings`). Sinon, rien à cliquer.
 */
export function apiFailureActionFor(code: ApiErrorCode): PageAccessAction | undefined {
  if (isPageAccessDeniedCode(code)) {
    return pageAccessActionFor(code);
  }
  switch (code) {
    case 'schedule_quota_exceeded':
    case 'premium_required':
    case 'subscription_not_found':
    case 'subscription_not_active':
      return 'upgrade';
    case 'linkedin_not_connected':
    case 'linkedin_token_expired':
      return 'reconnect';
    case 'team_not_member':
    case 'team_role_not_allowed':
    case 'team_member_not_found':
    case 'scheduled_author_not_allowed':
      return 'teamSettings';
    default:
      return undefined;
  }
}
